import React from "react";
import { Link } from "react-router-dom";
import Nav from "../site/Nav";
import SEO from "../site/SEO";
import Footer from "../site/Footer";
import ReadingProgress from "../site/ReadingProgress";
import useScrollReveal from "../../hooks/useScrollReveal";

export default function TermsPage() {
  useScrollReveal();
  return (
    <div className="bg-sand-100 text-ink min-h-screen" data-testid="terms-page">
      <SEO page="/terms" />
      <ReadingProgress />
      <Nav />
      <main className="pt-24 sm:pt-28 md:pt-32 pb-20">
        <article className="max-w-3xl mx-auto px-5 sm:px-6 md:px-10">
          <header className="reveal">
            <p className="eyebrow !text-clay">Legal</p>
            <h1 data-kinetic className="font-display mt-5 text-4xl sm:text-5xl md:text-6xl leading-[1.05] tracking-[-0.02em] font-semibold text-ink">
              Terms of Service
            </h1>
            <p className="mt-4 font-mono text-[10.5px] uppercase tracking-[0.18em] text-ink-muted">
              Last updated · February 2026
            </p>
          </header>

          <div className="mt-12 reveal prose prose-sm max-w-none text-ink
              prose-headings:font-display prose-headings:tracking-tight prose-headings:text-ink
              prose-h2:text-[1.4rem] prose-h2:mt-10 prose-h2:mb-4
              prose-h3:text-[1.1rem] prose-h3:mt-6 prose-h3:mb-2
              prose-p:text-[15px] prose-p:leading-relaxed prose-p:text-ink-muted
              prose-li:text-[15px] prose-li:text-ink-muted
              prose-strong:text-ink prose-a:text-clay hover:prose-a:text-rust">

            <p>
              These Terms of Service (&ldquo;Terms&rdquo;) govern your access
              to and use of The Modern Exit website (<a href="https://themodernexit.net">themodernexit.net</a>)
              and any website-build, revenue-system, or advisory services we
              provide (the &ldquo;Services&rdquo;). By using the site or
              engaging us, you agree to these Terms.
            </p>

            <h2>Eligibility</h2>
            <p>
              You must be at least 18 years old and authorized to enter into a
              binding agreement on behalf of yourself or your business to use
              our Services.
            </p>

            <h2>Scope of services</h2>
            <p>
              We build productized websites and configure revenue-system
              packages (CRM, follow-up, booking, payments, reviews,
              reactivation). The exact scope, deliverables, and timeline for
              your project are defined in the Website Build Brief, accepted
              proposal, or Statement of Work you sign with us.
            </p>

            <h2>Payments &amp; refunds</h2>
            <ul>
              <li>Fees are stated in the package you select or the signed proposal.</li>
              <li>Payments are processed by Stripe. By submitting payment, you authorize the charge and agree to Stripe&rsquo;s terms.</li>
              <li>All fees are <strong>non-refundable</strong> once work has begun, except where required by law.</li>
              <li>If you cancel before kickoff, we will refund any amounts in excess of work already performed.</li>
            </ul>

            <h2>Client responsibilities</h2>
            <p>You agree to provide:</p>
            <ul>
              <li>Timely feedback within agreed turnaround windows.</li>
              <li>Accurate brand assets, copy, and approvals.</li>
              <li>Access to domain, hosting, CRM, and other accounts where needed.</li>
              <li>Authority to make decisions on behalf of your business.</li>
            </ul>
            <p>Delays in providing the above may extend the project timeline.</p>

            <h2>Intellectual property</h2>
            <p>
              Once your project is paid in full, you own the final deliverables
              built specifically for you. The Modern Exit retains ownership of
              underlying frameworks, templates, automation logic, and tooling
              used to build the deliverables. We may showcase anonymized
              versions of the work in our portfolio unless you request
              otherwise in writing.
            </p>

            <h2>Third-party platforms</h2>
            <p>
              Our Services may configure accounts on third-party platforms
              (Stripe, GoHighLevel, Resend, etc.). Your relationship with
              those platforms is governed by their own terms. We are not
              responsible for outages, pricing changes, or feature removals on
              those platforms.
            </p>

            <h2>No ranking guarantees</h2>
            <p>
              We build websites with sound SEO foundations, but we do not
              guarantee specific search rankings, traffic volumes, or revenue
              outcomes. Results depend on your market, competition, content,
              and execution beyond our direct control.
            </p>

            <h2>Disclaimer of warranties</h2>
            <p>
              The site and Services are provided &ldquo;as is&rdquo; and
              &ldquo;as available.&rdquo; To the fullest extent permitted by
              law, we disclaim all warranties, express or implied, including
              merchantability, fitness for a particular purpose, and
              non-infringement.
            </p>

            <h2>Limitation of liability</h2>
            <p>
              In no event will The Modern Exit be liable for any indirect,
              incidental, consequential, special, or punitive damages. Our
              total liability for any claim is limited to the amount you paid
              us in the twelve (12) months preceding the claim.
            </p>

            <h2>Termination</h2>
            <p>
              Either party may terminate an engagement for material breach
              with 10 days&rsquo; written notice. Upon termination you will
              pay for all work performed through the termination date.
            </p>

            <h2>Governing law</h2>
            <p>
              These Terms are governed by the laws of the State of Texas,
              United States, without regard to conflict-of-law principles.
              Disputes will be resolved in the state or federal courts located
              in Dallas County, Texas.
            </p>

            <h2>Changes to these Terms</h2>
            <p>
              We may update these Terms from time to time. Material changes
              will be reflected in the &ldquo;Last updated&rdquo; date above
              and, where appropriate, announced by email or on this page.
            </p>

            <h2>Contact</h2>
            <p>
              Questions about these Terms? Email{" "}
              <a href="mailto:info@themodernexit.net">info@themodernexit.net</a>{" "}
              or call{" "}
              <a href="tel:+12142539779">+1 (214) 253-9779</a>.
            </p>
          </div>

          <div className="mt-14 reveal">
            <Link
              to="/"
              data-testid="terms-back-home"
              className="inline-flex items-center gap-2 font-mono text-[10.5px] uppercase tracking-[0.18em] text-clay hover:text-rust"
            >
              ← Back to home
            </Link>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
}
